import {
  DashboardTimeZone,
  useDashboardSearch,
  type DashboardZone,
} from 'corso-admin-ui';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { useStoreUser } from '~/providers/StoreUserProvider';
import { useUserOrgs } from '~/providers/UserProvider';

const merchantSearch = {
  org: z.coerce.number().int().optional(),
  cur: z.string().optional(),
};

// the first is the default: store time, for a store with a zone on file
const zonesFor = (storeZone: string | undefined) =>
  match(storeZone)
    .returnType<[DashboardZone, ...DashboardZone[]]>()
    .with(undefined, () => [{ key: DashboardTimeZone.local }])
    // local time would repeat store time for a viewer in the store's zone
    .with(Intl.DateTimeFormat().resolvedOptions().timeZone, (iana) => [
      { key: DashboardTimeZone.store, iana },
    ])
    .otherwise((iana) => [
      { key: DashboardTimeZone.store, iana },
      { key: DashboardTimeZone.local },
    ]);

/** Dashboard selections live in the URL, so they survive tab changes, reloads and shared links. */
export function useDashboardParams() {
  const { search, update, tz, ...shared } = useDashboardSearch(merchantSearch);

  const {
    store: { ianaTimezone },
  } = useStoreUser();
  // only an organization the viewer belongs to, never Corso's; a link naming another shows the store
  const organizations = useUserOrgs().filter(({ storeIds }) => storeIds.length);
  const organization =
    organizations.find(({ organizationId }) => organizationId === search.org) ??
    null;
  // an organization's stores keep their own zones, so none of them is its store time
  const timeZones = zonesFor(organization ? undefined : ianaTimezone);
  const [defaultZone] = timeZones;

  return {
    ...shared,
    timeZones,
    // a link asking for store time at a store with none gets the default, as one that asks for nothing does
    timeZone: timeZones.find(({ key }) => key === tz) ?? defaultZone,
    setTimeZone: (zone: DashboardTimeZone) =>
      update({ tz: zone === defaultZone.key ? null : zone }),
    organizations,
    organization,
    setOrganization: (organizationId: number | null) =>
      update({ org: organizationId === null ? null : `${organizationId}` }),
    // the shop currency an organization is read in; one its stores don't use reads as the first they do
    currency: search.cur ?? null,
    setCurrency: (currency: string) => update({ cur: currency }),
  };
}
